// Which model wrote a Swim Coach conversation's replies.
//
// The backend records the model on each coach reply it writes
// (SwimCoachMessage.aiModel), so a model trial can be read conversation by
// conversation. The model is not stored on the conversation itself, so both the
// list and the detail view derive it from the replies.

// Mirrors SwimCoachMessageDao.FIELD.AI_MODEL in the backend.
export const MODEL_FIELD = 'aiModel'

// Why a conversation can show no model. Worth stating wherever the empty state
// appears, otherwise it reads as a broken query.
export const MODEL_NOTE =
  'The model is recorded on coach replies written since Oct 22, 2026. ' +
  'Older replies, and the post-workout opener (written by workout analysis, ' +
  'not the chat model), carry none.'

// Family, major, optional minor, then an optional dated-snapshot suffix. The
// minor is capped at two digits so a snapshot date is never read as one:
// 'claude-sonnet-4-20250514' is Sonnet 4, not Sonnet 4.20250514.
const MODEL_ID_PATTERN = /^claude-([a-z]+)-(\d+)(?:-(\d{1,2}))?(?:-\d{8})?$/

// One colour per model family, so a list that mixes them can be scanned.
const FAMILY_BADGE_CLASS = {
  haiku: 'text-bg-info',
  sonnet: 'text-bg-secondary',
  opus: 'text-bg-dark'
}

// 'claude-haiku-5-5' -> 'Haiku 5.5'. Any other id is shown as it is, so a model
// outside the Claude naming scheme still reads.
export function modelLabel (modelId) {
  const match = MODEL_ID_PATTERN.exec(modelId || '')
  if (!match) {
    return modelId || ''
  }
  const family = match[1].charAt(0).toUpperCase() + match[1].slice(1)
  const version = match[3] ? match[2] + '.' + match[3] : match[2]
  return family + ' ' + version
}

export function modelBadgeClass (modelId) {
  const match = MODEL_ID_PATTERN.exec(modelId || '')
  // Bordered: a plain light badge vanishes on the light assistant card and on
  // the list's striped rows.
  return (match && FAMILY_BADGE_CLASS[match[1]]) || 'text-bg-light border'
}

// Appends the message's model unless it is missing or already listed, which
// keeps each model at the position it first appeared.
function addModel (models, message) {
  const model = message.get(MODEL_FIELD)
  if (model && !models.includes(model)) {
    models.push(model)
  }
}

// The distinct models on a run of messages, in the order they first appear.
// Pass messages oldest first. More than one model means the conversation moved
// part-way, e.g. it was already under way when a trial started.
export function modelsUsed (messages) {
  const models = []
  messages.forEach((message) => addModel(models, message))
  return models
}

// Groups replies from many conversations into models per conversation id, for
// the list view's single batched query. Same ordering rule as modelsUsed.
export function modelsByConversation (messages) {
  const byConversation = {}
  messages.forEach((message) => {
    const conversation = message.get('conversation')
    if (!conversation || !message.get(MODEL_FIELD)) {
      return
    }
    const models = byConversation[conversation.id] || []
    addModel(models, message)
    byConversation[conversation.id] = models
  })
  return byConversation
}
